/**
 * CDN platform filter — a button that opens a dialog listing the service's CDN
 * platforms grouped by their power-up category (Basic / Global / Regional CDN, …),
 * as toggle chips. Multi-select; no selection = all platforms.
 *
 * Categories come from `useWafPowerUpCategories` (id → title), matched to each
 * platform's `category_id`; uncategorised platforms fall under "Other".
 */
import { useEffect, useMemo, useState } from 'react';
import { Boxes, Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
} from '@vnetwork/ui/primitives';
import { useWafPlatforms, useWafPowerUpCategories, powerUpCategoryNames } from '@/api/waf';
import styles from './WafPlatformFilterDialog.module.css';

/** Extra, fixed platform groups not in the CDN catalog (appended last). */
const EXTRA_GROUPS: { category: string; items: { id: string; label: string }[] }[] = [
  { category: 'Monitoring CDN', items: [{ id: 'N0000000196', label: 'VNCDN' }] },
];

/** id → display label for the extra platforms (for chart legend labelling). */
export const EXTRA_PLATFORM_LABELS: Record<string, string> = Object.fromEntries(
  EXTRA_GROUPS.flatMap((g) => g.items.map((it) => [it.id, it.label])),
);

/** Monitoring VNCDN — always part of the default selection. */
const VNCDN_MONITORING_ID = 'N0000000196';
/** Well-known CDNs, in rough popularity order (matched against name/display_name). */
const FAMOUS_CDNS = [
  'cloudflare', 'akamai', 'cloudfront', 'fastly', 'gcore', 'g-core', 'alibaba', 'aliyun',
  'tencent', 'baishan', 'chunghwa', 'gma', 'edgeone', 'azure', 'bunny', 'cdn77', 'keycdn',
];
const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * The default platform selection: VNCDN (monitoring) plus the most popular CDNs
 * present in the catalog, capped at `max`.
 */
export function defaultFamousPlatformIds(
  catalog: { id: string; name: string; display_name: string }[] = [],
  max = 10,
): string[] {
  const ids: string[] = [VNCDN_MONITORING_ID];
  const ranked = catalog
    .map((p) => ({ id: p.id, rank: FAMOUS_CDNS.findIndex((f) => norm(`${p.name} ${p.display_name}`).includes(norm(f))) }))
    .filter((p) => p.rank >= 0)
    .sort((a, b) => a.rank - b.rank);
  for (const p of ranked) {
    if (ids.length >= max) break;
    if (!ids.includes(p.id)) ids.push(p.id);
  }
  return ids;
}

export function WafPlatformFilterDialog({
  serviceId,
  value,
  onChange,
}: {
  serviceId: string;
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  // Stage selections locally; only commit to the parent (→ refetch) on Apply, so
  // toggling chips doesn't fire a query per click (avoids upstream 429s).
  const [draft, setDraft] = useState<string[]>(value);
  // Re-seed the draft from the committed value whenever the dialog opens.
  useEffect(() => {
    if (open) setDraft(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const platformsQ = useWafPlatforms(serviceId);
  const categoriesQ = useWafPowerUpCategories(serviceId);
  const catNames = useMemo(() => powerUpCategoryNames(categoriesQ.data), [categoriesQ.data]);

  // Group platforms by category title, preserving the category tree order.
  const groups = useMemo(() => {
    const byCat = new Map<string, { id: string; label: string }[]>();
    for (const p of platformsQ.data ?? []) {
      const cat = (p.category_id != null ? catNames.get(p.category_id) : undefined) || 'Other';
      const arr = byCat.get(cat) ?? [];
      arr.push({ id: p.id, label: p.display_name || p.name });
      byCat.set(cat, arr);
    }
    const order = [...new Set([...catNames.values(), 'Other'])];
    const catalog = order
      .filter((c) => byCat.has(c))
      .map((c) => ({ category: c, items: byCat.get(c)!.sort((a, b) => a.label.localeCompare(b.label)) }));
    return [...catalog, ...EXTRA_GROUPS];
  }, [platformsQ.data, catNames]);

  const selected = useMemo(() => new Set(draft), [draft]);
  const toggle = (id: string) => setDraft(selected.has(id) ? draft.filter((x) => x !== id) : [...draft, id]);
  const apply = () => { onChange(draft); setOpen(false); };
  const triggerLabel = value.length ? `Platforms (${value.length})` : 'All platforms';

  return (
    <>
      <Button variant="secondary" startIcon={<Boxes size={15} />} onClick={() => setOpen(true)}>
        {triggerLabel}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>CDN platforms</DialogTitle>
            <DialogDescription>
              Filter the chart by CDN platform, grouped by category. No selection = all platforms.
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--vn-space-2)', minHeight: 28 }}>
              {draft.length > 0 && (
                <Button size="sm" variant="ghost" onClick={() => setDraft([])}>Clear all</Button>
              )}
            </div>
            {groups.length === 0 ? (
              <span className={styles.empty}>No platforms available.</span>
            ) : (
              groups.map((g) => (
                <div key={g.category} className={styles.group}>
                  <p className={styles.groupHeader}>{g.category}</p>
                  <div className={styles.chips}>
                    {g.items.map((it) => {
                      const on = selected.has(it.id);
                      return (
                        <Button
                          key={it.id}
                          size="sm"
                          variant={on ? undefined : 'secondary'}
                          startIcon={on ? <Check size={14} /> : undefined}
                          onClick={() => toggle(it.id)}
                        >
                          {it.label}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={apply}>Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default WafPlatformFilterDialog;
